<template>
    <div ref="slot" class="match-video" :class="{ 'match-video--open': open }">
        <!-- closed: a slim card with the thumbnail -->
        <button v-if="!open" type="button" class="video-card" @click="play">
            <span class="video-card__thumb" :style="thumbnail ? { backgroundImage: `url(${thumbnail})` } : null">
                <span class="video-card__play"><v-icon color="white">mdi-play</v-icon></span>
            </span>
            <span class="video-card__copy">
                <span v-if="live" class="video-card__live"><i />LIVE</span>
                <span class="video-card__title">{{ live ? 'Ζωντανή μετάδοση' : 'Δες ολόκληρο τον αγώνα' }}</span>
                <span class="video-card__sub">
                    <v-icon x-small>mdi-youtube</v-icon>
                    Βίντεο στο YouTube · παίζει όσο βλέπεις τα στατιστικά
                </span>
            </span>
            <v-icon class="video-card__chevron">mdi-chevron-right</v-icon>
        </button>

        <!-- open: the player keeps its place, and docks to a corner when scrolled out of view -->
        <div v-else class="video-frame-slot">
            <div class="video-player" :class="{ 'video-player--docked': docked }">
                <div class="video-player__bar">
                    <span class="video-player__label">
                        <span v-if="live" class="video-card__live"><i />LIVE</span>
                        {{ docked ? 'Ο αγώνας' : (live ? 'Ζωντανή μετάδοση' : 'Ολόκληρος ο αγώνας') }}
                    </span>
                    <button v-if="docked" type="button" class="video-player__btn" title="Επιστροφή στο βίντεο" @click="scrollBack">
                        <v-icon small color="white">mdi-arrow-expand</v-icon>
                    </button>
                    <button type="button" class="video-player__btn" title="Κλείσιμο" @click="close">
                        <v-icon small color="white">mdi-close</v-icon>
                    </button>
                </div>
                <div class="video-player__frame">
                    <iframe
                        :src="playerUrl"
                        title="Βίντεο αγώνα"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // The match video under the header: a card that opens the player in place,
    // and a mini player in the corner once the page is scrolled past it
    export default {
        name: 'MatchVideo',
        props: {
            url: {
                type: String,
                required: true
            },
            live: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                open: false,
                docked: false,
                observer: null
            }
        },
        computed: {
            youtubeId() {
                const match = String(this.url).match(/(?:youtube\.com\/(?:embed\/|watch\?v=|live\/)|youtu\.be\/)([\w-]{11})/)
                return match ? match[1] : null
            },
            thumbnail() {
                return this.youtubeId ? `https://img.youtube.com/vi/${this.youtubeId}/hqdefault.jpg` : null
            },
            // the click on the card is the user's go-ahead, so the video starts right away
            playerUrl() {
                return `${this.url}${this.url.includes('?') ? '&' : '?'}autoplay=1&playsinline=1`
            }
        },
        watch: {
            url() {
                this.close()
            }
        },
        beforeDestroy() {
            this.stopWatching()
        },
        methods: {
            play() {
                this.open = true
                this.$nextTick(this.startWatching)
            },
            close() {
                this.open = false
                this.docked = false
                this.stopWatching()
            },
            startWatching() {
                if (typeof IntersectionObserver === 'undefined' || !this.$refs.slot) return
                this.stopWatching()
                // docked while less than a third of the player's place is on screen
                this.observer = new IntersectionObserver(([entry]) => {
                    this.docked = this.open && entry.intersectionRatio < 0.35
                }, { threshold: [0, 0.35, 1] })
                this.observer.observe(this.$refs.slot)
            },
            stopWatching() {
                if (this.observer) {
                    this.observer.disconnect()
                    this.observer = null
                }
            },
            scrollBack() {
                const top = this.$refs.slot.getBoundingClientRect().top + window.scrollY - 140
                window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
            }
        }
    }
</script>

<style scoped>
.match-video {
    margin-bottom: 28px;
    font-family: Roboto, Arial, sans-serif;
}

/* ---------- Card ---------- */
.video-card {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 10px 16px 10px 10px;
    border-radius: 12px;
    background: #232323;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    color: #fff;
    text-align: left;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.video-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.2);
}

.video-card__thumb {
    position: relative;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 128px;
    aspect-ratio: 16 / 9;
    border-radius: 8px;
    background: #2b2b2e center / cover no-repeat;
    overflow: hidden;
}

.video-card__play {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
    transition: transform 0.15s ease;
}

.video-card:hover .video-card__play {
    transform: scale(1.1);
}

.video-card__copy {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
    min-width: 0;
}

.video-card__title {
    font-size: 1.05rem;
    font-weight: 800;
}

.video-card__sub {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.78rem;
    font-weight: 600;
}

.video-card__sub .v-icon {
    color: inherit;
}

.video-card__chevron {
    color: rgba(255, 255, 255, 0.6) !important;
}

.video-card__live {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.06em;
}

.video-card__live i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: video-pulse 1.4s ease-in-out infinite;
}

@keyframes video-pulse {
    0%,
    100% { opacity: 1; }
    50% { opacity: 0.3; }
}

/* ---------- Player ---------- */

/* keeps the room of the player while it is docked, so the page does not jump */
.video-frame-slot {
    aspect-ratio: 16 / 9;
    max-height: 70vh;
    margin: 0 auto;
}

.video-player {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: #232323;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18);
    overflow: hidden;
}

.video-player__bar {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 6px 8px 6px 14px;
    color: #fff;
}

.video-player__label {
    display: inline-flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
    font-size: 0.85rem;
    font-weight: 800;
    white-space: nowrap;
}

.video-player__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
}

.video-player__btn:hover {
    background: rgba(255, 255, 255, 0.24);
}

.video-player__frame {
    position: relative;
    flex: 1 1 auto;
}

.video-player__frame iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}

/* mini player: bottom left, so it never covers the scroll-to-top button */
.video-player--docked {
    position: fixed;
    bottom: 20px;
    left: 20px;
    z-index: 20;
    width: 360px;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
    animation: video-dock 0.25s ease-out;
}

.video-player--docked .video-player__frame {
    aspect-ratio: 16 / 9;
}

.video-player--docked .video-player__bar {
    padding: 4px 6px 4px 10px;
}

.video-player--docked .video-player__label {
    font-size: 0.75rem;
}

@keyframes video-dock {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: none;
    }
}

@media (max-width: 600px) {
    .video-card {
        gap: 12px;
        padding: 8px 10px 8px 8px;
    }

    .video-card__thumb {
        width: 96px;
    }

    .video-card__play {
        width: 32px;
        height: 32px;
    }

    .video-card__title {
        font-size: 0.92rem;
    }

    .video-card__sub {
        font-size: 0.7rem;
    }

    .video-player--docked {
        bottom: calc(14px + env(safe-area-inset-bottom, 0px));
        left: 10px;
        width: min(62vw, 260px);
    }
}
</style>
